import React, { useState } from 'react';
import MainStructure from '../../components/general/MainStructure';
import { useAuth } from '../../AuthContext';

// ─── Plan definitions (must match pricing.php + Stripe config) ───────────────
const PLANS = [
  {
    id: 'silver',
    name: 'Silver',
    monthly: 29,
    yearly: 21,
    yearlyTotal: 252,
    savingsPerYear: 96,
    features: [
      '5 Connected Accounts',
      '1 Workspace',
      'Post Scheduling',
      'Post editor with images',
      'Post carousels',
      'Account Analytics',
      'AI Post Suggestions',
      'API Access',
    ],
  },
  {
    id: 'gold',
    name: 'Gold',
    monthly: 39,
    yearly: 35,
    yearlyTotal: 420,
    savingsPerYear: 48,
    popular: true,
    features: [
      '20 Connected Accounts',
      '5 Workspaces',
      'Post Scheduling',
      'Post editor with images',
      'AI Generated Posts & Hooks',
      'Reply to posts & comments',
      '300 GPT-5 Credits',
      'API Access',
    ],
  },
  {
    id: 'platinum',
    name: 'Platinum',
    monthly: 59,
    yearly: 49,
    yearlyTotal: 588,
    savingsPerYear: 120,
    features: [
      '40 Connected Accounts',
      '10 Workspaces',
      'Post Scheduling',
      'Chrome Extension',
      'PRO Support',
      '5,100 GPT-5 Credits',
      'API Access',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthly: 99,
    yearly: 79,
    yearlyTotal: 948,
    savingsPerYear: 240,
    features: [
      '100 Connected Accounts',
      '20 Workspaces',
      '10 Team Seats',
      'Post Approval Workflow',
      'Dedicated Account Manager',
      'Priority Support',
      '10,000 GPT-5 Credits',
      'API Access',
    ],
  },
];

const PLAN_ORDER = ['silver', 'gold', 'platinum', 'enterprise'];

const PLAN_ACCENT = {
  silver:     { border: '#94a3b8', badge: '#64748b',  bg: '#f8fafc' },
  gold:       { border: '#5ba400', badge: '#5ba400',  bg: '#f0fdf4' },
  platinum:   { border: '#22c55e', badge: '#16a34a',  bg: '#f0fdf4' },
  enterprise: { border: '#4f46e5', badge: '#4f46e5',  bg: '#eef2ff' },
};

// ─── Small helpers ────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="#16a34a" style={{ flexShrink: 0, marginTop: 2 }}>
      <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
    </svg>
  );
}

function planLabel(planId) {
  return PLANS.find(p => p.id === planId)?.name ?? planId;
}

function planRank(planId) {
  return PLAN_ORDER.indexOf(planId);
}

// ─── Main component ───────────────────────────────────────────────────────────

function Billing() {
  const { membership, isValidMembership } = useAuth();
  const [billing, setBilling]             = useState('yearly'); // 'monthly' | 'yearly'
  const [showConfirm, setShowConfirm]     = useState(false);
  const [showReason, setShowReason]       = useState(false);
  const [cancelReason, setCancelReason]   = useState('');
  const [cancelling, setCancelling]       = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError]     = useState('');

  // Normalise membership object (API sometimes returns array)
  const mem = Array.isArray(membership) ? membership[0] : membership;
  const hasMembership  = isValidMembership();
  const isCancelled    = mem?.subscription_status === 'cancelled';
  // Set by the invoice.payment_failed webhook when a renewal charge is declined.
  const isPastDue      = mem?.subscription_status === 'past_due';
  const currentPlanId  = mem?.plan?.plan   ?? null;   // 'silver' | 'gold' | ...
  const currentType    = mem?.plan?.type   ?? null;   // 'monthly' | 'yearly' | 'lifetime'
  const renewsOn       = mem?.end_date_time
    ? new Date(mem.end_date_time).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  // Lifetime members never renew and can't be cancelled. Detect either an
  // explicit 'lifetime' type or an end date more than 10 years out (the
  // far-future sentinel used by admin-granted lifetime memberships).
  const isLifetime = currentType === 'lifetime' || (
    mem?.end_date_time
      ? new Date(mem.end_date_time.replace(' ', 'T') + 'Z').getTime() > Date.now() + 10 * 365 * 24 * 3600 * 1000
      : false
  );

  // True only for users who have NEVER had a membership before.
  // Cancelled or expired users must pay immediately — no second trial.
  const isFreeTrialEligible = !hasMembership && !isCancelled && currentPlanId === null;

  /* ── Update payment method ──
     Opens Stripe's billing portal so the card is replaced on the EXISTING
     subscription. Going through checkout again would create a second one. */
  const handleUpdateCard = () => {
    setPortalLoading(true);
    fetch('https://api.schedpilot.com/wp-json/api/v1/billing-portal', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(res => res.json())
      .then(json => {
        if (json.code === 200 && json.url) {
          window.location.href = json.url;
          return;
        }
        setPortalError(json.message || 'Could not open the billing portal. Please try again.');
        setPortalLoading(false);
      })
      .catch(() => {
        setPortalError('Network error. Please try again.');
        setPortalLoading(false);
      });
  };

  /* ── Cancel ── */
  const handleConfirmCancel = () => {
    if (!cancelReason.trim()) return;
    setCancelling(true);
    fetch('https://api.schedpilot.com/wp-json/api/v1/cancel-membership', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: cancelReason.trim() }),
    })
      .then(r => r.json())
      .then(() => {
        setShowReason(false);
        setShowConfirm(false);
        setCancelling(false);
        setCancelReason('');
        alert('Membership cancelled. You still have access until your current period ends.');
      })
      .catch(() => {
        setCancelling(false);
        alert('Something went wrong. Please try again.');
      });
  };

  /* ── Upgrade / choose plan ── */
  const handleChoosePlan = (planId, type, trial = false) => {
    const url = `https://api.schedpilot.com/?choose_plan=${planId}&type=${type}${trial ? '&trial=1' : ''}`;
    window.location.href = url;
  };

  /* ── Plan card CTA label & style ── */
  const getPlanCta = (plan) => {
    if (!hasMembership || isCancelled) {
      return {
        label:    isFreeTrialEligible ? 'Start for $0' : 'Get started',
        variant:  'primary',
        disabled: false,
        trial:    isFreeTrialEligible,
      };
    }
    const rank    = planRank(plan.id);
    const curRank = planRank(currentPlanId);

    if (plan.id === currentPlanId) {
      return { label: 'Current plan', variant: 'current', disabled: true };
    }
    if (rank > curRank) {
      return { label: '↑ Upgrade', variant: 'upgrade', disabled: false };
    }
    return { label: '↓ Downgrade', variant: 'downgrade', disabled: false };
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <MainStructure activeItem="billing">
      <div style={{ padding: '32px 24px' }}>

        {/* Page header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: '1.45rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Billing &amp; Plans</h1>
          <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '4px 0 0' }}>
            Manage your subscription and upgrade your plan.
          </p>
        </div>

        {/* ── Current plan card ── */}
        {hasMembership && currentPlanId && !isCancelled && (
          <div style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderLeft: `4px solid ${PLAN_ACCENT[currentPlanId]?.border ?? '#94a3b8'}`,
            borderRadius: 14,
            padding: '20px 24px',
            marginBottom: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 16,
          }}>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: 4 }}>
                Active subscription
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#0f172a' }}>
                  SchedPilot {planLabel(currentPlanId)}
                </span>
                <span style={{
                  background: PLAN_ACCENT[currentPlanId]?.bg ?? '#f1f5f9',
                  color: PLAN_ACCENT[currentPlanId]?.badge ?? '#64748b',
                  border: `1px solid ${PLAN_ACCENT[currentPlanId]?.border ?? '#94a3b8'}`,
                  borderRadius: 999,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  padding: '2px 10px',
                  textTransform: 'capitalize',
                }}>
                  {isLifetime ? '★ Lifetime' : currentType}
                </span>
              </div>
              {isLifetime ? (
                <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#16a34a', fontWeight: 600 }}>
                  ✨ Lifetime access — never expires
                </p>
              ) : renewsOn && (
                <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                  Renews on <strong style={{ color: '#334155' }}>{renewsOn}</strong>
                </p>
              )}
            </div>
            {!isLifetime && (
              <button
                onClick={() => setShowConfirm(true)}
                style={{
                  background: 'none',
                  border: '1px solid #fca5a5',
                  color: '#ef4444',
                  borderRadius: 8,
                  padding: '7px 16px',
                  fontSize: '0.82rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel membership
              </button>
            )}
          </div>
        )}

        {/* Cancelled banner */}
        {isPastDue && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 28,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: '1.2rem' }}>⚠️</span>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#991b1b' }}>
                <strong>Your last payment didn't go through.</strong> Update your payment method
                to keep your subscription active — your scheduled posts and connected accounts
                are all still here.
              </p>
            </div>

            <button
              onClick={handleUpdateCard}
              disabled={portalLoading}
              style={{
                background: '#dc2626', color: '#fff', border: 'none', borderRadius: 9,
                padding: '10px 20px', fontWeight: 600, fontSize: '0.875rem',
                cursor: portalLoading ? 'default' : 'pointer',
              }}
            >
              {portalLoading ? 'Opening…' : 'Update payment method'}
            </button>

            {portalError && (
              <p style={{ margin: '10px 0 0', fontSize: '0.8rem', color: '#991b1b' }}>{portalError}</p>
            )}
          </div>
        )}

        {isCancelled && renewsOn && (
          <div style={{
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 28,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{ fontSize: '1.2rem' }}>⚠️</span>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400e' }}>
              Your <strong>{planLabel(currentPlanId)}</strong> subscription is cancelled.{' '}
              You still have access until <strong>{renewsOn}</strong>. Pick a plan below to resubscribe.
            </p>
          </div>
        )}

        {/* No membership banner */}
        {!hasMembership && !isCancelled && !isLifetime && (
          <div style={{
            background: isFreeTrialEligible ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${isFreeTrialEligible ? '#bbf7d0' : '#fecaca'}`,
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 28,
          }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: isFreeTrialEligible ? '#166534' : '#991b1b' }}>
              {isFreeTrialEligible
                ? '🎉 You\'re eligible for a 7-day free trial. Pick a plan below to get started — no charge until the trial ends.'
                : 'You don\'t have an active membership. Choose a plan below to resubscribe.'}
            </p>
          </div>
        )}

        {/* ── Lifetime members: no plan picker, just a thank-you card ── */}
        {isLifetime ? (
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <div style={{
              background: 'linear-gradient(135deg, #fffbeb, #fff)',
              border: '1px solid #fcd34d',
              borderRadius: 16,
              padding: '32px 28px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '2.2rem', marginBottom: 8 }}>★</div>
              <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>
                You have a Lifetime {planLabel(currentPlanId)} membership
              </h2>
              <p style={{ fontSize: '0.92rem', color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                Your access never expires and you'll never be billed again. Thank you for being one of
                our lifetime members! 🎉 If you ever need anything, just reach out to support.
              </p>
            </div>
          </div>
        ) : (
        <div style={{ maxWidth: 1024, margin: '0 auto' }}>

        {/* ── Billing toggle ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>Billing:</span>
          <div style={{
            display: 'inline-flex',
            background: '#f1f5f9',
            borderRadius: 999,
            padding: 4,
            gap: 4,
          }}>
            {['monthly', 'yearly'].map(mode => (
              <button
                key={mode}
                onClick={() => setBilling(mode)}
                style={{
                  border: 'none',
                  borderRadius: 999,
                  padding: '6px 16px',
                  fontSize: '0.82rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  background: billing === mode ? '#fff' : 'transparent',
                  color:      billing === mode ? '#0f172a' : '#64748b',
                  boxShadow:  billing === mode ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                }}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
                {mode === 'yearly' && (
                  <span style={{ marginLeft: 6, fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>
                    Save 20%+
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Plan cards grid ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
        }}>
          {PLANS.map(plan => {
            const cta      = getPlanCta(plan);
            const isCurrent = hasMembership && plan.id === currentPlanId && !isCancelled;
            const accent   = PLAN_ACCENT[plan.id];
            const price    = billing === 'monthly' ? plan.monthly : plan.yearly;

            return (
              <div
                key={plan.id}
                style={{
                  background: '#fff',
                  border: isCurrent ? `2px solid ${accent.border}` : '1px solid #e2e8f0',
                  borderRadius: 16,
                  padding: '22px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  boxShadow: isCurrent ? `0 0 0 3px ${accent.bg}` : 'none',
                  transition: 'box-shadow 0.15s',
                }}
              >
                {/* Popular badge */}
                {plan.popular && !isCurrent && (
                  <div style={{
                    position: 'absolute',
                    top: -11,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(90deg, #5ba400, #22c55e)',
                    color: '#fff',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    padding: '3px 12px',
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                  }}>
                    Most Popular
                  </div>
                )}

                {/* Current badge */}
                {isCurrent && (
                  <div style={{
                    position: 'absolute',
                    top: -11,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: accent.border,
                    color: '#fff',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    padding: '3px 12px',
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                  }}>
                    Your plan
                  </div>
                )}

                {/* Plan name */}
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a', marginBottom: 4 }}>
                  {plan.name}
                </div>

                {/* Price */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, marginBottom: 2 }}>
                  <span style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, color: plan.id === 'enterprise' ? '#4f46e5' : '#5ba400' }}>
                    ${price}
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 4 }}>/ mo</span>
                </div>

                {/* Yearly billing note */}
                {billing === 'yearly' ? (
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 14 }}>
                    ${plan.yearlyTotal} billed yearly &nbsp;·&nbsp;
                    <span style={{ color: '#16a34a', fontWeight: 600 }}>Save ${plan.savingsPerYear}/yr</span>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 14 }}>
                    Billed monthly
                  </div>
                )}

                {/* CTA button */}
                <PlanButton cta={cta} onClick={() => handleChoosePlan(plan.id, billing, cta.trial)} accentColor={accent.border} />

                {/* Free trial note */}
                {isFreeTrialEligible && !cta.disabled && (
                  <div style={{ textAlign: 'center', fontSize: '0.75rem', color: '#16a34a', fontWeight: 500, marginTop: 6 }}>
                    7-day free trial · cancel anytime
                  </div>
                )}

                {/* Divider */}
                <div style={{ borderTop: '1px solid #f1f5f9', margin: '16px 0 12px' }} />

                {/* Features */}
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.875rem', color: '#1e293b' }}>
                      <CheckIcon />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* ── View full pricing page ── */}
        <div style={{
          marginTop: 24,
          padding: '16px 20px',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div>
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f172a' }}>
              Want to compare all features in detail?
            </span>
            <span style={{ fontSize: '0.875rem', color: '#64748b', marginLeft: 8 }}>
              View the full pricing page with a complete plan breakdown.
            </span>
          </div>
          <a
            href="https://schedpilot.com/pricing"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#334155',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              transition: 'box-shadow 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.10)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}
          >
            View full pricing
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>

        </div>
        )}{/* end plans section */}

        {/* ── Cancel confirmation modal ── */}
        {showConfirm && (
          <div
            onClick={() => setShowConfirm(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1050,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: '28px 32px',
                maxWidth: 420,
                width: '90%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
              }}
            >
              <h5 style={{ fontWeight: 700, marginBottom: 8, color: '#0f172a' }}>Cancel membership?</h5>
              <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 24 }}>
                Your subscription will be cancelled and you'll keep access until{' '}
                <strong style={{ color: '#334155' }}>{renewsOn}</strong>. No more charges after that.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowConfirm(false)}
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 500, cursor: 'pointer', fontSize: '0.875rem', color: '#374151' }}
                >
                  Keep membership
                </button>
                <button
                  onClick={() => { setShowConfirm(false); setShowReason(true); }}
                  style={{ background: '#ef4444', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 500, cursor: 'pointer', fontSize: '0.875rem', color: '#fff' }}
                >
                  Yes, cancel it
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Cancellation reason modal (last step) ── */}
        {showReason && (
          <div
            onClick={() => { if (!cancelling) setShowReason(false); }}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1050,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: '28px 32px',
                maxWidth: 460,
                width: '90%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
              }}
            >
              <h5 style={{ fontWeight: 700, marginBottom: 8, color: '#0f172a' }}>One last thing</h5>
              <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 16 }}>
                We promise this is the last step before cancellation — we just need some feedback to improve the product.
              </p>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="What made you decide to cancel?"
                rows={4}
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d9e0', borderRadius: 10, padding: '10px 12px', fontSize: '0.9rem', color: '#0f172a', outline: 'none', resize: 'vertical', marginBottom: 20 }}
              />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowReason(false); setCancelReason(''); }}
                  disabled={cancelling}
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 500, cursor: 'pointer', fontSize: '0.875rem', color: '#374151' }}
                >
                  Keep membership
                </button>
                <button
                  onClick={handleConfirmCancel}
                  disabled={cancelling || !cancelReason.trim()}
                  style={{ background: '#ef4444', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 500, cursor: (cancelling || !cancelReason.trim()) ? 'default' : 'pointer', fontSize: '0.875rem', color: '#fff', opacity: (cancelling || !cancelReason.trim()) ? 0.6 : 1 }}
                >
                  {cancelling ? 'Cancelling…' : 'Cancel membership'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </MainStructure>
  );
}

// ─── Plan CTA button ──────────────────────────────────────────────────────────

function PlanButton({ cta, onClick, accentColor }) {
  const base = {
    width: '100%',
    border: 'none',
    borderRadius: 9,
    padding: '9px 0',
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: cta.disabled ? 'default' : 'pointer',
    transition: 'filter 0.15s',
  };

  const styles = {
    current: {
      ...base,
      background: '#f1f5f9',
      color: '#64748b',
      border: '1px solid #e2e8f0',
    },
    upgrade: {
      ...base,
      background: 'linear-gradient(135deg, #16a34a, #22c55e)',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(22,163,74,0.25)',
    },
    downgrade: {
      ...base,
      background: 'none',
      color: '#64748b',
      border: '1px solid #e2e8f0',
    },
    primary: {
      ...base,
      background: 'linear-gradient(135deg, #5ba400, #22c55e)',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(91,164,0,0.25)',
    },
  };

  return (
    <button
      style={styles[cta.variant] ?? styles.primary}
      disabled={cta.disabled}
      onClick={cta.disabled ? undefined : onClick}
      onMouseEnter={e => { if (!cta.disabled) e.currentTarget.style.filter = 'brightness(0.93)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
    >
      {cta.label}
    </button>
  );
}

export default Billing;
